import { TextInput, useGetOne } from "react-admin";
import { FormProvider, useForm } from "react-hook-form";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { Box, Link, Typography } from "@mui/material";
import InfoOutlineIcon from "@mui/icons-material/InfoOutlined";
import { ConfirmDialog } from "../ConfirmDialog";
import { Student } from "../../providers/studentsProvider";
import { StartingPointInput } from "../../inputs/StartingPointInput";
import { Instructor } from "../../providers/instructorsProvider";
import { DrivingLessonDurationInput } from "../../inputs/DrivingLessonDurationInput";
import { DrivingLicenseClassInput } from "../../inputs/DrivingLicenseClassInput";
import { DateTimeInput } from "../../inputs/DateTimeInput";
import { StudentInputField } from "./StudentInputField";
import { VehiclesInput } from "../../inputs/VehiclesInput";
import {
  DrivingLicenseClass,
  isForBus,
  isForCar,
  isForMotorcycle,
  isForTractor,
  isForTruck,
  isWithTrailer,
} from "../../model/DrivingLicenseClass";
import { instructorRecommendationSettingsProvider } from "../../providers/instructorRecommendationSettingsProvider";
import { Column } from "../../misc/Column";
import { Row } from "../../misc/Row";
import { DrivingLessonTypeInput } from "../../inputs/DrivingLessonTypeInput";
import { StartingPoint, startingPointsProvider } from "../../providers/startingPointsProvider";
import { StudentSuggestions } from "./StudentSuggestions";
import { PriceSuggestion } from "./PriceSuggestion";
import { DrivingLessonRepetitionsField } from "./DrivingLessonRepetitionsField";
import { BookCalendarEventWithoutInviteButton } from "../../buttons/BookCalendarEventWithoutInviteButton";
import { grants } from "../../backoffice.access_control";
import { Form2Theme } from "../../misc/Form2";
import { handleBookingError } from "../../utils/handleBookingError";
import { useNightLessonInDaylightWarning } from "../../hooks/useNightLessonInDaylightWarning";
import { WhenFormIsInitialized } from "../../misc/WhenFormIsInitialized";
import { InstructorInput } from "../../inputs/InstructorInput";
import { CreateCalendarEventDto, serverAPI } from "../../api/server.api";
import { autovioColors } from "../../misc/backofficeTheme";
import { VehicleType } from "../../model/VehicleType";
import { BufferTimesInput } from "../../inputs/BufferTimesInput";
import { BufferTimes } from "../../model/BufferTimes";
import { SaveButton2 } from "../../buttons/SaveButton2";
import { useGetDrivingLessonStartingPoint } from "../../hooks/useGetCalendarEventStartingPoint";
import { z } from "zod";
import { DrivingLessonTypeEnum } from "../../model/DrivingLessonType";
import { assertNever } from "../../backoffice.utils";

// This component handles only actual driving lessons (not every calendar event of type "DrivingLesson").
// So, it's not covering simulator and practicalExam calendar events.
const supportedDrivingLessonTypeEnum = DrivingLessonTypeEnum.extract([
  "normal",
  "ueberlandfahrt",
  "autobahnfahrt",
  "nachtfahrt",
  "schaltkompetenz",
  "unterweisungAmFahrzeug",
  "praktischeUnterweisung",
]);
type supportedDrivingLessonType = z.infer<typeof supportedDrivingLessonTypeEnum>;

export interface CreateDrivingLessonFormValues {
  drivingLessonType: supportedDrivingLessonType;
  dateTime?: DateTime;
  durationInMinutes: number;
  bufferTimes: BufferTimes;
  startingPointId: string;
  instructorId: string;
  student?: Student;
  drivingLicenseClass: "" | DrivingLicenseClass;
  carId: string | null;
  motorcycleId: string | null;
  truckId: string | null;
  busId: string | null;
  tractorId: string | null;
  trailerId: string | null;
  secondMotorcycleOrCarId: string | null;
  repetitions: DateTime[];
  remarks?: string;
}

export interface BookDrivingLessonParams {
  dateTime: DateTime;
  bufferTimes: BufferTimes;
  drivingLessonType: supportedDrivingLessonType;
  instructorId: string;
  studentId: string;
  bookedTrainingId: string;
  duration: { minutes: number };
  startingPointId: string;
  vehicleId: string | null;
  vehicle2Id: string | null;
  repetitions: DateTime[];
  remarks?: string;
}

export const DRIVING_LESSON_DURATION_OPTIONS = [15, 30, 45, 60, 75, 90, 120, 135, 180, 210, 225];

export function CreateDrivingLessonForm(props: {
  instructor?: Instructor;
  student?: Student;
  vehicles?: Array<{ id: string; type: VehicleType }>;
  drivingLicenseClass?: DrivingLicenseClass;
  startingPoint?: StartingPoint;
  initialDateTime?: DateTime;
  isHistoricalLesson?: boolean;
  onSuccess: () => void;
}) {
  const {
    instructor: initialInstructor,
    student: initialStudent,
    vehicles,
    drivingLicenseClass: initialDrivingLicenseClass,
    startingPoint: initialStartingPoint,
    initialDateTime,
    isHistoricalLesson = false,
    onSuccess,
  } = props;
  const [insufficientPrepaidCredit, setInsufficientPrepaidCredit] = useState<boolean>(false);
  const [showBufferTimes, setShowBufferTimes] = useState(false);
  const [showStudentRemarks, setShowStudentRemarks] = useState(false);
  const [showRepetitions, setShowRepetitions] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState<{
    missingVehicles: string[];
    studentRsvp: "pending" | "accepted";
  } | null>(null);

  const [vehicle1, vehicle2] = vehicles ?? [];
  const defaultValues = {
    drivingLessonType: "normal",
    dateTime: initialDateTime,
    durationInMinutes: 0, // ... overridden with preferredDurationMinsPerDrivingLessonType, see below
    bufferTimes: { preparationTime: 0, wrapUpTime: 0 },
    instructorId: initialInstructor?.id ?? "",
    student: initialStudent,
    carId: vehicle1?.type === "car" ? vehicle1.id : null,
    motorcycleId: vehicle1?.type === "motorcycle" ? vehicle1.id : null,
    truckId: vehicle1?.type === "truck" ? vehicle1.id : null,
    busId: vehicle1?.type === "bus" ? vehicle1.id : null,
    tractorId: vehicle1?.type === "tractor" ? vehicle1.id : null,
    trailerId: vehicle1?.type === "trailer" ? vehicle1.id : null,
    secondMotorcycleOrCarId: vehicle2?.id ?? null,
    drivingLicenseClass: initialDrivingLicenseClass ?? "",
    startingPointId: initialStartingPoint?.id ?? "",
    repetitions: [],
  } satisfies CreateDrivingLessonFormValues;
  const formProps = useForm<CreateDrivingLessonFormValues>({
    defaultValues: async () => {
      let durationInMinutes = defaultValues.durationInMinutes;
      if (initialInstructor) {
        const { data } = await instructorRecommendationSettingsProvider.getOne("", { id: initialInstructor.id });
        if (data) {
          durationInMinutes = data.drivingLessonDuration;
        }
      }
      return { ...defaultValues, durationInMinutes };
    },
  });
  const formValues = formProps.watch();
  const { student, instructorId, dateTime, drivingLessonType, drivingLicenseClass, startingPointId, repetitions } =
    formValues;
  const nightLessonInDaylightWarning = useNightLessonInDaylightWarning(formValues);
  const { data: selectedInstructor } = useGetOne<Instructor>(
    "instructors",
    { id: instructorId },
    { enabled: !initialInstructor && !!instructorId },
  );
  const instructor = initialInstructor || selectedInstructor;

  const { startingPoint, isLoading: isLoadingStartingPoint } = useGetDrivingLessonStartingPoint(student);

  // Update the starting point ID once it is available, or when the student changes.
  useEffect(() => {
    if (startingPoint && startingPoint.id !== startingPointId) {
      formProps.setValue("startingPointId", startingPoint.id);
    }
  }, [startingPoint, formProps.setValue]);

  function validate(formValues: CreateDrivingLessonFormValues): BookDrivingLessonParams {
    const {
      drivingLessonType,
      dateTime,
      durationInMinutes,
      bufferTimes,
      instructorId,
      student,
      drivingLicenseClass,
      startingPointId,
      remarks,
    } = formValues;
    if (!dateTime) {
      throw new Error(`Invalid dateTime: ${dateTime}`);
    }
    if (!drivingLessonType) {
      throw new Error(`Invalid drivingLessonType: ${drivingLessonType}`);
    }
    if (!student) {
      throw new Error(`Invalid student: ${student}`);
    }
    if (!instructorId) {
      throw new Error(`Invalid instructorId: ${instructorId}`);
    }
    if (!drivingLicenseClass) {
      throw new Error(`Invalid drivingLicenseClass: ${drivingLicenseClass}`);
    }
    const vehicleFields = getVehicleFields(drivingLicenseClass, formValues);
    let vehicleId: string | null = null;
    let vehicle2Id: string | null = null;
    for (const field of vehicleFields) {
      if (field.slot === "vehicleId") {
        vehicleId = field.id;
      } else if (field.slot === "vehicle2Id") {
        vehicle2Id = field.id;
      } else {
        assertNever(field.slot);
      }
    }
    const bookedTraining = student.bookedTrainings.find((it) => it.drivingLicenseClass === drivingLicenseClass);
    if (!bookedTraining) {
      throw new Error(`Student ${student.id} has no booked training for driving license class ${drivingLicenseClass}`);
    }

    if (!supportedDrivingLessonTypeEnum.safeParse(drivingLessonType).success) {
      throw new Error(`Invalid drivingLessonType: ${drivingLessonType}`);
    }
    if (!startingPointId) {
      throw new Error(`Invalid startingPointId: ${startingPointId}`);
    }
    if (!durationInMinutes) {
      throw new Error(`Invalid durationInMinutes: ${durationInMinutes}`);
    }
    if (formValues.repetitions.some((date) => date.hasSame(dateTime, "day"))) {
      throw new Error("Invalid lesson repetition conflicting with main lesson time");
    }
    const repetitions =
      drivingLessonType === "schaltkompetenz"
        ? []
        : formValues.repetitions.map((date) =>
            date.startOf("day").set({ hour: dateTime.hour, minute: dateTime.minute }),
          );

    return {
      drivingLessonType,
      instructorId,
      studentId: student.id,
      bookedTrainingId: bookedTraining.id,
      dateTime,
      duration: { minutes: durationInMinutes },
      bufferTimes,
      startingPointId,
      vehicleId,
      vehicle2Id,
      repetitions,
      remarks,
    } satisfies BookDrivingLessonParams;
  }

  const onSubmit =
    ({ studentRsvp }: { studentRsvp: "pending" | "accepted" }) =>
    async (formValues: CreateDrivingLessonFormValues) => {
      const missingVehicles = getMissingVehicles(formValues);
      if (missingVehicles.length > 0) {
        setPendingSubmit({ missingVehicles, studentRsvp });
        return;
      }
      await submit(formValues, studentRsvp);
    };

  const submit = async (formValues: CreateDrivingLessonFormValues, studentRsvp: "pending" | "accepted") => {
    try {
      const {
        dateTime,
        drivingLessonType,
        instructorId,
        studentId,
        bookedTrainingId,
        duration,
        bufferTimes,
        startingPointId,
        vehicleId,
        vehicle2Id,
        repetitions,
        remarks,
      } = validate(formValues);
      const isInThePast = dateTime < DateTime.now();
      const { data: startingPoint } = await startingPointsProvider.getOne("ignored", { id: startingPointId });
      const location = startingPoint.postalAddress;
      const payload = {
        id: uuidv4(),
        type: "DrivingLesson" as const,
        drivingLessonType,
        instructorId,
        start: dateTime.toISO(),
        end: dateTime.plus(duration).toISO(),
        bufferTimes,
        student: {
          bookedTrainingId: bookedTrainingId,
          id: studentId,
          rsvp: studentRsvp,
        },
        startLocation: location,
        vehicleId,
        vehicle2Id,
        remarks,
        ...(isHistoricalLesson && {
          manualStudentGross: { amount: 0, currency: "EUR" },
          endedAt: DateTime.now().toISO(),
        }),
      } satisfies CreateCalendarEventDto;
      await serverAPI.createCalendarEvent(payload);
      if (repetitions.length > 0) {
        let successfulRepetitionsCount = 0;
        try {
          for (const repetition of repetitions) {
            await serverAPI.createCalendarEvent({
              ...payload,
              id: uuidv4(),
              start: repetition.toISO(),
              end: repetition.plus(duration).toISO(),
            });
            successfulRepetitionsCount++;
          }
          showSuccessToast(`${repetitions.length + 1} Einladungen erfolgreich versendet.`);
        } catch (error) {
          handleError(
            `${repetitions.length - successfulRepetitionsCount} von ${repetitions.length + 1} Einladungen konnten nicht versendet werden.`,
            error,
          );
        }
        onSuccess();
        return;
      }
      showSuccessToast(
        studentRsvp === "accepted"
          ? "Fahrstunde erfolgreich gebucht."
          : isInThePast
            ? "Fahrstunde erfolgreich nachgetragen."
            : "Einladung erfolgreich versandt.",
      );
      onSuccess();
    } catch (error: any) {
      if (error?.response?.data?.type?.endsWith?.("student.not-enough-credit")) {
        setInsufficientPrepaidCredit(true);
      } else if (!(await handleBookingError(error, student))) {
        const errorMessage = isHistoricalLesson
          ? "Fehler beim Nachtragen der Fahrstunde"
          : studentRsvp === "accepted"
            ? "Fehler beim Buchen der Fahrstunde"
            : "Fehler beim Versenden der Einladung";
        handleError(errorMessage, error);
      }
    }
  };

  const areStudentRelatedInputsReady = student && !isLoadingStartingPoint;

  return (
    <Form2Theme>
      <FormProvider {...formProps}>
        <form
          onSubmit={formProps.handleSubmit(onSubmit({ studentRsvp: isHistoricalLesson ? "accepted" : "pending" }))}
          style={{ height: "100%" }}
        >
          <Column height="100%" minHeight={500} gap="1em">
            <WhenFormIsInitialized>
              <DateTimeInput
                source="dateTime"
                size="small"
                allowPastDates
                warning={nightLessonInDaylightWarning}
                allowFutureDates={!isHistoricalLesson}
              />
              {isHistoricalLesson && areStudentRelatedInputsReady && (
                <InstructorInput source="instructorId" size="small" />
              )}
              {!isHistoricalLesson && instructor && <StudentInputField instructorId={instructor.id} size="small" />}

              {areStudentRelatedInputsReady ? (
                <>
                  <DrivingLicenseClassInput
                    student={student}
                    source="drivingLicenseClass"
                    label="Ausbildung für Führerscheinklasse"
                    size="small"
                    showFinishedBookedTrainings={
                      !!(
                        formValues.dateTime &&
                        DateTime.isDateTime(formValues.dateTime) &&
                        formValues.dateTime.isValid &&
                        formValues.dateTime < DateTime.now()
                      )
                    }
                  />
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: drivingLicenseClass ? "minmax(0, 1fr) minmax(0, 2fr)" : "minmax(0, 1fr)",
                      gap: 2,
                      alignItems: "flex-start",
                      width: "100%",
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <DrivingLessonDurationInput
                        source="durationInMinutes"
                        options={DRIVING_LESSON_DURATION_OPTIONS}
                        unitLabel="Min."
                        size="small"
                        fullWidth
                        sx={{ minWidth: 0, "& .MuiInputBase-root": { minWidth: 0 } }}
                      />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      {drivingLicenseClass && (
                        <DrivingLessonTypeInput
                          source="drivingLessonType"
                          size="small"
                          fullWidth
                          sx={{ minWidth: 0, "& .MuiInputBase-root": { minWidth: 0 } }}
                        />
                      )}
                    </Box>
                  </Box>
                  {showBufferTimes ? (
                    <BufferTimesInput
                      source="bufferTimes"
                      preparationTimeLabel="Anfahrt"
                      wrapUpTimeLabel="Abfahrt"
                      size="small"
                    />
                  ) : (
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      sx={{ alignSelf: "flex-start" }}
                      onClick={() => setShowBufferTimes(true)}
                    >
                      An- und Abfahrtszeit hinzufügen
                    </Link>
                  )}
                  {instructor && drivingLicenseClass && (
                    <VehiclesInput
                      instructorId={instructor.id}
                      drivingLessonType={drivingLessonType}
                      drivingLicenseClass={drivingLicenseClass}
                      size="small"
                    />
                  )}
                  {instructor && (
                    <StartingPointInput
                      source="startingPointId"
                      drivingLessonType={drivingLessonType}
                      instructorId={instructor.id}
                      student={student}
                      size="small"
                    />
                  )}
                  {showStudentRemarks ? (
                    <TextInput source="remarks" label="Hinweis für Fahrschüler" multiline rows={2} />
                  ) : (
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      sx={{ alignSelf: "flex-start" }}
                      onClick={() => setShowStudentRemarks(true)}
                    >
                      Hinweis für Fahrschüler hinzufügen
                    </Link>
                  )}
                  {!isHistoricalLesson &&
                    vehicle1?.type !== "simulator" &&
                    drivingLessonType !== "schaltkompetenz" &&
                    dateTime &&
                    (showRepetitions || repetitions.length > 0 ? (
                      <DrivingLessonRepetitionsField openOnMount={showRepetitions && repetitions.length === 0} />
                    ) : (
                      <Link
                        component="button"
                        type="button"
                        variant="body2"
                        sx={{ alignSelf: "flex-start" }}
                        onClick={() => setShowRepetitions(true)}
                      >
                        Terminserie erstellen
                      </Link>
                    ))}
                  {!isHistoricalLesson && <PriceSuggestion validate={validate} />}
                  {insufficientPrepaidCredit && (
                    <Row gap="4px" alignItems="center">
                      <InfoOutlineIcon sx={{ fill: autovioColors.red, fontSize: "18px" }} />
                      <Typography variant="body2" color="error">
                        {student.name} hat nicht genug Guthaben.
                      </Typography>
                    </Row>
                  )}
                </>
              ) : (
                <StudentSuggestions />
              )}
              <Row sx={{ justifyContent: "flex-end", mt: "auto", gap: 1 }}>
                {!isHistoricalLesson && grants.has("DrivingLesson:bookWithoutInvite") && (
                  <BookCalendarEventWithoutInviteButton
                    eventType="drivingLesson"
                    onSubmit={formProps.handleSubmit(onSubmit({ studentRsvp: "accepted" }))}
                  />
                )}
                <SaveButton2 label={dateTime && dateTime < DateTime.now() ? "Termin nachtragen" : "Einladung senden"} />
              </Row>
            </WhenFormIsInitialized>
          </Column>
        </form>
      </FormProvider>
      <ConfirmDialog
        open={!!pendingSubmit}
        title={`Du erstellst eine Fahrstunde ohne ${(pendingSubmit?.missingVehicles ?? []).join(" und ohne ")}`}
        message="Möchtest du den Termin trotzdem erstellen?"
        confirmButtonLabel="Termin trotzdem erstellen"
        onClose={() => setPendingSubmit(null)}
        onConfirm={async () => {
          if (!pendingSubmit) return;
          await submit(formProps.getValues(), pendingSubmit.studentRsvp);
          setPendingSubmit(null);
        }}
      />
    </Form2Theme>
  );
}

function getMissingVehicles(formValues: CreateDrivingLessonFormValues): string[] {
  const { drivingLicenseClass } = formValues;
  if (!drivingLicenseClass) {
    return [];
  }
  return getVehicleFields(drivingLicenseClass, formValues)
    .filter((field) => !field.id)
    .map((field) => field.label);
}

interface VehicleField {
  id: string | null;
  label: string;
  slot: "vehicleId" | "vehicle2Id";
}

/**
 * Returns the vehicle fields for the given driving license class, paired with their current value
 * from the form. Used by both `validate()` (to assemble the submitted payload) and `getMissingVehicles()`
 * (to show the confirmation dialog) so both stay in sync.
 */
function getVehicleFields(
  drivingLicenseClass: DrivingLicenseClass,
  formValues: CreateDrivingLessonFormValues,
): VehicleField[] {
  const { carId, motorcycleId, truckId, busId, tractorId, trailerId, secondMotorcycleOrCarId } = formValues;
  let fields: VehicleField[];
  if (isForMotorcycle(drivingLicenseClass)) {
    fields = [
      { id: motorcycleId, label: "Motorrad", slot: "vehicleId" },
      { id: secondMotorcycleOrCarId, label: "Begleitfahrzeug für Fahrlehrer", slot: "vehicle2Id" },
    ];
  } else if (isForCar(drivingLicenseClass)) {
    fields = [{ id: carId, label: "PKW", slot: "vehicleId" }];
  } else if (isForTruck(drivingLicenseClass)) {
    fields = [{ id: truckId, label: "LKW", slot: "vehicleId" }];
  } else if (isForBus(drivingLicenseClass)) {
    fields = [{ id: busId, label: "Bus", slot: "vehicleId" }];
  } else if (isForTractor(drivingLicenseClass)) {
    fields = [{ id: tractorId, label: "Traktor", slot: "vehicleId" }];
  } else {
    throw new Error(`Unexpected drivingLicenseClass: ${JSON.stringify(drivingLicenseClass)}`);
  }
  if (isWithTrailer(drivingLicenseClass)) {
    fields.push({ id: trailerId, label: "Anhänger", slot: "vehicle2Id" });
  }
  return fields;
}
